import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { chargeTemplateRepository } from '../repositories/charge-template.repository';
// =============================================================================
// STORE
// =============================================================================
export const useChargeTemplateStore = defineStore('chargeTemplate', () => {
    // ── STATE ──────────────────────────────────────────────────────────────────
    const templates = ref([]);
    const editingId = ref(null);
    const pendingDeleteId = ref(null);
    const deleteBlockedBy = ref([]);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    let scopeVersion = 0;
    // ── GETTERS ────────────────────────────────────────────────────────────────
    const hasTemplates = computed(() => templates.value.length > 0);
    const templateById = computed(() => (id) => templates.value.find((t) => t.id === id) ?? null);
    // ── ACTIONS ────────────────────────────────────────────────────────────────
    async function fetchTemplates() {
        const version = scopeVersion;
        isLoading.value = true;
        error.value = null;
        try {
            const data = await chargeTemplateRepository.getAll();
            if (version !== scopeVersion)
                return;
            templates.value = data;
        }
        catch {
            if (version === scopeVersion)
                error.value = 'Error al cargar los cargos';
        }
        finally {
            if (version === scopeVersion)
                isLoading.value = false;
        }
    }
    function startCreate() {
        editingId.value = 'new';
    }
    function startEdit(id) {
        editingId.value = id;
    }
    function cancelEdit() {
        editingId.value = null;
    }
    async function saveTemplate(dto) {
        isSaving.value = true;
        error.value = null;
        try {
            if (editingId.value === 'new') {
                const created = await chargeTemplateRepository.create(dto);
                templates.value = [...templates.value, created];
            }
            else if (editingId.value !== null) {
                const updated = await chargeTemplateRepository.update(editingId.value, dto);
                templates.value = templates.value.map((t) => (t.id === updated.id ? updated : t));
            }
            editingId.value = null;
        }
        catch (err) {
            const message = extractErrorMessage(err) ?? 'Error al guardar el cargo';
            error.value = message;
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    function requestDelete(id) {
        pendingDeleteId.value = id;
    }
    function cancelDelete() {
        pendingDeleteId.value = null;
        deleteBlockedBy.value = [];
    }
    async function confirmDelete() {
        if (pendingDeleteId.value === null)
            return;
        const id = pendingDeleteId.value;
        isSaving.value = true;
        error.value = null;
        try {
            await chargeTemplateRepository.delete(id);
            templates.value = templates.value.filter((t) => t.id !== id);
            pendingDeleteId.value = null;
        }
        catch (err) {
            const usages = extractUsages(err);
            if (usages) {
                deleteBlockedBy.value = usages;
                pendingDeleteId.value = null;
            }
            else {
                error.value = 'Error al eliminar el cargo';
                throw err;
            }
        }
        finally {
            isSaving.value = false;
        }
    }
    function clearDeleteBlocked() {
        deleteBlockedBy.value = [];
    }
    function $reset() {
        scopeVersion++;
        templates.value = [];
        editingId.value = null;
        pendingDeleteId.value = null;
        deleteBlockedBy.value = [];
        isLoading.value = false;
        isSaving.value = false;
        error.value = null;
    }
    // ── RETURN ─────────────────────────────────────────────────────────────────
    return {
        templates,
        editingId,
        pendingDeleteId,
        deleteBlockedBy,
        isLoading,
        isSaving,
        error,
        hasTemplates,
        templateById,
        fetchTemplates,
        startCreate,
        startEdit,
        cancelEdit,
        saveTemplate,
        requestDelete,
        cancelDelete,
        confirmDelete,
        clearDeleteBlocked,
        $reset,
    };
});
// =============================================================================
// HELPERS
// =============================================================================
function extractErrorMessage(err) {
    if (err && typeof err === 'object' && 'response' in err) {
        const response = err.response;
        const msg = response?.data?.message;
        if (Array.isArray(msg))
            return msg[0];
        if (typeof msg === 'string')
            return msg;
    }
    return null;
}
function extractUsages(err) {
    if (err && typeof err === 'object' && 'response' in err) {
        const response = err.response;
        if (response?.status === 422 && Array.isArray(response?.data?.usages)) {
            return response.data.usages;
        }
    }
    return null;
}
